import { useState, useEffect } from "react";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Calculator, TrendingUp, DollarSign, Calendar, PiggyBank } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

type Scenario = 'conservative' | 'balanced' | 'optimistic';

interface ScenarioSettings {
  rentalYield: number;
  appreciationRate: number;
  label: string;
  description: string;
}

const scenarios: Record<Scenario, ScenarioSettings> = {
  conservative: {
    rentalYield: 8.5,
    appreciationRate: 5,
    label: 'Conservative',
    description: 'Low risk, guaranteed minimum returns'
  },
  balanced: {
    rentalYield: 10.5,
    appreciationRate: 7,
    label: 'Balanced',
    description: 'Moderate risk, market average returns'
  },
  optimistic: {
    rentalYield: 12.5,
    appreciationRate: 10,
    label: 'Optimistic',
    description: 'High growth potential scenario'
  }
};

export default function InvestmentCalculator() {
  const [investmentAmount, setInvestmentAmount] = useState(250000);
  const [investmentYears, setInvestmentYears] = useState(5);
  const [selectedScenario, setSelectedScenario] = useState<Scenario>('balanced');
  
  // Get current scenario settings
  const rentalYield = scenarios[selectedScenario].rentalYield;
  const appreciationRate = scenarios[selectedScenario].appreciationRate;

  // Calculations
  const annualRentalIncome = (investmentAmount * rentalYield) / 100;
  const totalRentalIncome = annualRentalIncome * investmentYears;
  const propertyValueAfter = investmentAmount * Math.pow(1 + appreciationRate / 100, investmentYears);
  const capitalGain = propertyValueAfter - investmentAmount;
  const totalReturn = totalRentalIncome + capitalGain;
  const totalROI = ((totalReturn / investmentAmount) * 100).toFixed(1);

  // Payment Plan Calculations (20% down, 80% over 3 years)
  const downPayment = investmentAmount * 0.2;
  const remainingAmount = investmentAmount * 0.8;
  const quarterlyPayment = remainingAmount / 12; // 12 quarters in 3 years

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Generate chart data for yearly breakdown
  const generateYearlyData = () => {
    const data = [];
    let cumulativeRental = 0;
    let propertyValue = investmentAmount;

    for (let year = 0; year <= investmentYears; year++) {
      if (year > 0) {
        cumulativeRental += annualRentalIncome;
        propertyValue = investmentAmount * Math.pow(1 + appreciationRate / 100, year);
      }
      
      data.push({
        year: year === 0 ? 'Start' : `Year ${year}`,
        rentalIncome: Math.round(cumulativeRental),
        propertyValue: Math.round(propertyValue),
        totalReturn: Math.round(cumulativeRental + (propertyValue - investmentAmount)),
      });
    }
    return data;
  };

  // Generate chart data for monthly breakdown (first year)
  const generateMonthlyData = () => {
    const monthlyRental = annualRentalIncome / 12;
    const data = [];
    
    for (let month = 1; month <= 12; month++) {
      data.push({
        month: `Month ${month}`,
        rental: Math.round(monthlyRental),
        cumulative: Math.round(monthlyRental * month),
      });
    }
    return data;
  };

  const yearlyData = generateYearlyData();
  const monthlyData = generateMonthlyData();

  return (
    <section className="py-20 bg-gradient-to-br from-primary/5 to-accent/5">
      <div className="container">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/20 text-accent-foreground rounded-full font-semibold text-sm mb-4">
              <Calculator className="w-4 h-4" />
              <span>INVESTMENT CALCULATOR</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Calculate Your High ROI Dubai Property Investment
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Estimate the returns on your Dubai real estate investment. This calculator helps you project your earnings from a high ROI Dubai apartment in Motor City, factoring in rental yield and property appreciation.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left: Calculator Controls */}
            <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold mb-8 text-card-foreground">Investment Parameters</h3>

              {/* Your USD Investment in Dubai Slider */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-base font-semibold">Your USD Investment in Dubai</Label>
                  <span className="text-2xl font-bold text-primary">{formatCurrency(investmentAmount)}</span>
                </div>
                <Slider
                  value={[investmentAmount]}
                  onValueChange={(value) => setInvestmentAmount(value[0])}
                  min={250000}
                  max={1000000}
                  step={10000}
                  className="mb-2"
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>$250K</span>
                  <span>$1M</span>
                </div>
              </div>

              {/* Scenario Selector */}
              <div className="mb-8">
                <Label className="text-base font-semibold mb-4 block">Dubai Property Market Scenario</Label>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {(Object.keys(scenarios) as Scenario[]).map((scenario) => (
                    <button
                      key={scenario}
                      onClick={() => setSelectedScenario(scenario)}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        selectedScenario === scenario
                          ? 'border-primary bg-primary/10 shadow-md'
                          : 'border-border bg-card hover:border-primary/50'
                      }`}
                    >
                      <div className="font-semibold text-sm text-card-foreground">
                        {scenarios[scenario].label}
                      </div>
                    </button>
                  ))}
                </div>
                <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
                  <div className="text-sm font-semibold text-accent-foreground mb-2">
                    {scenarios[selectedScenario].label} Scenario
                  </div>
                  <div className="text-xs text-muted-foreground mb-3">
                    {scenarios[selectedScenario].description}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Rental Yield:</span>
                      <span className="font-bold text-primary ml-2">{rentalYield}%</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Appreciation:</span>
                      <span className="font-bold text-primary ml-2">{appreciationRate}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Investment Duration */}
              <div className="mb-8">
                <Label className="text-base font-semibold mb-4 block">Investment Duration</Label>
                <Select
                  value={investmentYears.toString()}
                  onValueChange={(value) => setInvestmentYears(parseInt(value))}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 Years</SelectItem>
                    <SelectItem value="5">5 Years</SelectItem>
                    <SelectItem value="7">7 Years</SelectItem>
                    <SelectItem value="10">10 Years</SelectItem>
                  </SelectContent>
                </Select>
              </div>


            </div>

            {/* Right: Results */}
            <div className="space-y-6">
              {/* Total Return on Your Dubai Investments Card */}
              <div className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground rounded-2xl p-8 shadow-xl">
                <div className="flex items-center gap-3 mb-4">
                  <TrendingUp className="w-8 h-8" />
                  <h3 className="text-xl font-semibold">Total Return on Your Dubai Investment</h3>
                </div>
                <div className="text-5xl font-bold mb-2">{formatCurrency(totalReturn)}</div>
                <div className="text-lg opacity-90">ROI: {totalROI}%</div>
                <div className="mt-4 pt-4 border-t border-primary-foreground/20 text-sm opacity-80">
                  After {investmentYears} years on {formatCurrency(investmentAmount)} investment
                </div>
              </div>

              {/* Breakdown Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-3 text-muted-foreground">
                    <DollarSign className="w-5 h-5" />
                    <span className="text-sm font-semibold">10% Rental Income Dubai</span>
                  </div>
                  <div className="text-2xl font-bold text-card-foreground mb-1">
                    {formatCurrency(totalRentalIncome)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatCurrency(annualRentalIncome)}/year
                  </div>
                </div>

                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-3 text-muted-foreground">
                    <TrendingUp className="w-5 h-5" />
                    <span className="text-sm font-semibold">Tax-Free Capital Gains</span>
                  </div>
                  <div className="text-2xl font-bold text-card-foreground mb-1">
                    {formatCurrency(capitalGain)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Property value: {formatCurrency(propertyValueAfter)}
                  </div>
                </div>
              </div>

              {/* Payment Plan Card */}
              <div className="bg-accent/10 border border-accent/30 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <PiggyBank className="w-5 h-5 text-accent-foreground" />
                  <h4 className="font-semibold text-accent-foreground">Flexible Payment Plan</h4>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Down Payment (20%)</span>
                    <span className="font-bold text-card-foreground">{formatCurrency(downPayment)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Quarterly Payment (36 months)</span>
                    <span className="font-bold text-card-foreground">{formatCurrency(quarterlyPayment)}</span>
                  </div>
                  <div className="pt-3 border-t border-border">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-card-foreground">Total Property Price</span>
                      <span className="font-bold text-lg text-primary">{formatCurrency(investmentAmount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Benefits */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h4 className="font-semibold mb-4 text-card-foreground">Your Investment Includes:</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">✓</span>
                    <span>0% property tax, 0% income tax, 0% capital gains tax</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">✓</span>
                    <span>Target 10% rental yield (historical average in similar projects)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">✓</span>
                    <span>Full property management & tenant placement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">✓</span>
                    <span>Golden Visa eligibility for you and your family</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

              {/* Charts Section */}
              <div className="mt-12 space-y-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold mb-2 text-foreground">Visual Breakdown</h3>
                  <p className="text-muted-foreground">See how your investment grows over time</p>
                </div>

                {/* Yearly Growth Chart */}
                <div className="bg-card border border-border rounded-2xl p-6 shadow-lg">
                  <h4 className="text-lg font-semibold mb-6 text-card-foreground">Yearly Investment Growth</h4>
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={yearlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="year" 
                        stroke="hsl(var(--muted-foreground))"
                        style={{ fontSize: '12px' }}
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        style={{ fontSize: '12px' }}
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: 'blur(12px)',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          borderRadius: '12px',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          padding: '12px 16px',
                          color: '#1a1a1a'
                        }}
                        labelStyle={{
                          color: '#1a1a1a',
                          fontWeight: '600',
                          marginBottom: '8px'
                        }}
                        itemStyle={{
                          color: '#1a1a1a',
                          fontWeight: '500'
                        }}
                        formatter={(value: number) => formatCurrency(value)}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="line"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="rentalIncome" 
                        name="Cumulative 10% Rental Income Dubai"
                        stroke="hsl(var(--primary))" 
                        strokeWidth={3}
                        dot={{ fill: 'hsl(var(--primary))', r: 5 }}
                        activeDot={{ r: 7 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="propertyValue" 
                        name="Property Value"
                        stroke="hsl(var(--accent))" 
                        strokeWidth={3}
                        dot={{ fill: 'hsl(var(--accent))', r: 5 }}
                        activeDot={{ r: 7 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="totalReturn" 
                        name="Total Return on Your Dubai Investment"
                        stroke="#10b981" 
                        strokeWidth={3}
                        strokeDasharray="5 5"
                        dot={{ fill: '#10b981', r: 5 }}
                        activeDot={{ r: 7 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Monthly 10% Rental Income Dubai Chart */}
                <div className="bg-card border border-border rounded-2xl p-6 shadow-lg">
                  <h4 className="text-lg font-semibold mb-6 text-card-foreground">Monthly 10% Rental Income Dubai (First Year)</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="month" 
                        stroke="hsl(var(--muted-foreground))"
                        style={{ fontSize: '11px' }}
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        style={{ fontSize: '12px' }}
                        tickFormatter={(value) => `$${(value / 1000).toFixed(1)}K`}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: 'blur(12px)',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          borderRadius: '12px',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          padding: '12px 16px',
                          color: '#1a1a1a'
                        }}
                        labelStyle={{
                          color: '#1a1a1a',
                          fontWeight: '600',
                          marginBottom: '8px'
                        }}
                        itemStyle={{
                          color: '#1a1a1a',
                          fontWeight: '500'
                        }}
                        formatter={(value: number) => formatCurrency(value)}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                      />
                      <Bar 
                        dataKey="rental" 
                        name="Monthly Rental"
                        fill="hsl(var(--primary))" 
                        radius={[8, 8, 0, 0]}
                      />
                      <Bar 
                        dataKey="cumulative" 
                        name="Cumulative Income"
                        fill="hsl(var(--accent))" 
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Disclaimer */}
          <div className="mt-8 text-center text-sm text-muted-foreground">
            <p>
              *These calculations are estimates based on current market conditions and historical trends. 
              Actual returns may vary. Rental yield guarantee applies for the first 3 years post-handover. 
              Consult with our investment specialists for personalized projections.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
